import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Trash2,
  Mail,
  RefreshCw,
  Plus,
  Pencil,
  Star,
  StarOff,
  Save,
  X,
  LogOut,
  Upload,
  Link as LinkIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [tab, setTab] = useState("inbox");
  const { user, logout } = useAuth();

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/[0.06] sticky top-0 bg-black/70 backdrop-blur-xl z-10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            data-testid="admin-back-link"
            className="inline-flex items-center gap-2 font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/70 hover:text-white"
          >
            <ArrowLeft size={14} /> Back to site
          </Link>
          <div className="flex items-center gap-2 font-display font-bold text-sm uppercase tracking-tight">
            <img
              src="/NaN-Logotype-05-300x169.png"
              alt="NaN"
              className="h-6 w-auto"
            />
            NaN / Admin
          </div>
          <div className="flex items-center gap-3">
            {user?.email && (
              <span className="hidden md:inline font-mono-accent text-[10px] uppercase tracking-[0.25em] text-white/40">
                {user.email}
              </span>
            )}
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 font-mono-accent text-[11px] uppercase tracking-[0.3em] text-white/70 hover:text-white"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center gap-1 border-t border-white/[0.04]">
          <TabBtn id="inbox" active={tab} setActive={setTab} label="Inbox" />
          <TabBtn id="projects" active={tab} setActive={setTab} label="Projects" />
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {tab === "inbox" ? <Inbox /> : <Projects />}
      </main>
    </div>
  );
}

function TabBtn({ id, active, setActive, label }) {
  const isActive = active === id;
  return (
    <button
      onClick={() => setActive(id)}
      data-testid={`admin-tab-${id}`}
      className={`relative px-4 py-3 font-mono-accent text-[11px] uppercase tracking-[0.3em] transition-colors ${
        isActive ? "text-white" : "text-white/50 hover:text-white/80"
      }`}
    >
      {label}
      {isActive && <span className="absolute left-0 right-0 -bottom-px h-px bg-[#29ABE2]" />}
    </button>
  );
}

/* ---------------------- INBOX TAB ---------------------- */
function Inbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contact`);
      setItems(data);
      if (data.length && !selected) setSelected(data[0]);
    } catch {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`);
      setItems((s) => s.filter((x) => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Submission deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <section className="lg:col-span-5 border border-white/[0.06]">
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/50">
            Submissions ({items.length})
          </div>
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/60 hover:text-white"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto no-scrollbar divide-y divide-white/[0.06]">
          {loading ? (
            <div className="p-10 text-center text-white/50 text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-white/50 text-sm">
              No submissions yet.
            </div>
          ) : (
            items.map((s) => (
              <button
                key={s.id}
                data-testid={`admin-item-${s.id}`}
                onClick={() => setSelected(s)}
                className={`w-full text-left px-5 py-4 hover:bg-white/[0.03] transition-colors ${
                  selected?.id === s.id ? "bg-white/[0.04]" : ""
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className="font-mono-accent text-[10px] text-white/40">
                    {new Date(s.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="text-xs text-white/55 truncate">{s.email}</div>
                <div className="text-xs text-white/45 truncate mt-1">{s.message}</div>
              </button>
            ))
          )}
        </div>
      </section>

      <section className="lg:col-span-7 border border-white/[0.06] min-h-[60vh]">
        {selected ? (
          <article className="p-8 md:p-10" data-testid="admin-detail">
            <div className="flex items-start justify-between gap-4 mb-8">
              <div>
                <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
                  {new Date(selected.created_at).toLocaleString()}
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                  {selected.name}
                </h2>
                <div className="text-white/60 text-sm mt-2">{selected.company || "—"}</div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`mailto:${selected.email}`}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#29ABE2] text-black text-xs font-medium hover:bg-[#5cc2ec]"
                >
                  <Mail size={12} /> Reply
                </a>
                <button
                  data-testid="admin-delete"
                  onClick={() => remove(selected.id)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-white/[0.1] text-white/80 hover:text-white text-xs"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>

            <dl className="grid grid-cols-2 md:grid-cols-3 gap-6 border-t border-b border-white/[0.06] py-6 mb-8 text-sm">
              <Meta label="Email" value={selected.email} />
              <Meta label="Service" value={selected.service || "—"} />
              <Meta label="Budget" value={selected.budget || "—"} />
            </dl>

            <div>
              <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
                Message
              </div>
              <p className="text-white/80 leading-relaxed whitespace-pre-wrap">
                {selected.message}
              </p>
            </div>
          </article>
        ) : (
          <div className="p-10 text-white/50 text-sm">Select a submission to view details.</div>
        )}
      </section>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <dt className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">
        {label}
      </dt>
      <dd className="text-white/85 break-all">{value}</dd>
    </div>
  );
}

/* ---------------------- PROJECTS TAB ---------------------- */
const EMPTY_PROJECT = {
  title: "",
  client: "",
  discipline: "",
  year: String(new Date().getFullYear()),
  image_url: "",
  description: "",
  case_url: "",
  featured: false,
  sort_order: 0,
};

function Projects() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY_PROJECT);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/projects`, { params: { limit: 200 } });
      setItems(data);
    } catch {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY_PROJECT);
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      title: p.title,
      client: p.client,
      discipline: p.discipline,
      year: p.year,
      image_url: p.image_url,
      description: p.description,
      case_url: p.case_url || "",
      featured: !!p.featured,
      sort_order: p.sort_order || 0,
    });
  };

  const cancel = () => {
    setEditing(null);
    setForm(EMPTY_PROJECT);
  };

  const save = async () => {
    if (!form.title || !form.client || !form.image_url || !form.description) {
      toast.error("Title, client, image URL and description are required.");
      return;
    }
    const payload = { ...form, case_url: form.case_url || null };
    try {
      if (editing === "new") {
        await axios.post(`${API}/projects`, payload);
        toast.success("Project created");
      } else {
        await axios.put(`${API}/projects/${editing}`, payload);
        toast.success("Project updated");
      }
      cancel();
      load();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg || "Save failed. Check inputs."
      );
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/projects/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleFeatured = async (p) => {
    try {
      await axios.put(`${API}/projects/${p.id}`, {
        title: p.title,
        client: p.client,
        discipline: p.discipline,
        year: p.year,
        image_url: p.image_url,
        description: p.description,
        case_url: p.case_url || null,
        featured: !p.featured,
        sort_order: p.sort_order || 0,
      });
      toast.success(p.featured ? "Removed from featured" : "Marked featured");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const featuredCount = items.filter((p) => p.featured).length;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <section className="lg:col-span-7 border border-white/[0.06]">
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between gap-4">
          <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/50">
            Projects ({items.length}) · Featured ({featuredCount}/3)
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-projects-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/60 hover:text-white"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              data-testid="admin-project-new"
              onClick={startNew}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#29ABE2] text-black text-[10px] uppercase tracking-[0.25em] font-mono-accent hover:bg-[#5cc2ec]"
            >
              <Plus size={12} /> New project
            </button>
          </div>
        </div>

        <div className="divide-y divide-white/[0.06] max-h-[75vh] overflow-y-auto no-scrollbar">
          {loading ? (
            <div className="p-10 text-center text-white/50 text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-white/50 text-sm">
              No projects yet. Click "New project" to add one.
            </div>
          ) : (
            items.map((p) => (
              <div
                key={p.id}
                data-testid={`admin-project-${p.id}`}
                className={`px-5 py-4 flex items-center gap-4 hover:bg-white/[0.03] transition-colors ${
                  editing === p.id ? "bg-white/[0.04]" : ""
                }`}
              >
                <img
                  src={p.image_url}
                  alt={p.title}
                  className="w-16 h-12 object-cover border border-white/[0.08]"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-sm truncate">{p.title}</div>
                    {p.featured && (
                      <span className="font-mono-accent text-[9px] uppercase tracking-[0.2em] text-[#29ABE2] border border-[#29ABE2]/40 rounded-full px-2 py-0.5">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-white/50 truncate">
                    {p.client} · {p.discipline} · {p.year}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <IconBtn
                    title={p.featured ? "Unfeature" : "Mark featured"}
                    onClick={() => toggleFeatured(p)}
                    testid={`admin-project-toggle-${p.id}`}
                  >
                    {p.featured ? (
                      <StarOff size={14} className="text-[#29ABE2]" />
                    ) : (
                      <Star size={14} />
                    )}
                  </IconBtn>
                  <IconBtn
                    title="Edit"
                    onClick={() => startEdit(p)}
                    testid={`admin-project-edit-${p.id}`}
                  >
                    <Pencil size={14} />
                  </IconBtn>
                  <IconBtn
                    title="Delete"
                    onClick={() => remove(p.id)}
                    testid={`admin-project-delete-${p.id}`}
                  >
                    <Trash2 size={14} />
                  </IconBtn>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="lg:col-span-5 border border-white/[0.06] min-h-[60vh]">
        {editing ? (
          <ProjectForm
            form={form}
            setForm={setForm}
            onSave={save}
            onCancel={cancel}
            isNew={editing === "new"}
          />
        ) : (
          <div className="p-10 text-white/50 text-sm">
            Select a project to edit, or create a new one.
          </div>
        )}
      </section>
    </div>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="p-2 rounded-full border border-white/[0.08] text-white/70 hover:text-white hover:border-white/30 transition-colors"
    >
      {children}
    </button>
  );
}

function ProjectForm({ form, setForm, onSave, onCancel, isNew }) {
  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const [imgMode, setImgMode] = useState(() =>
    form.image_url?.startsWith("data:") ? "upload" : "url"
  );
  const [processing, setProcessing] = useState(false);
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB).");
      return;
    }
    setProcessing(true);
    const reader = new FileReader();
    reader.onerror = () => {
      setProcessing(false);
      toast.error("Could not read file.");
    };
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const MAX_W = 1600;
        const scale = Math.min(1, MAX_W / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        // Hard cap on payload — ~4MB encoded
        if (dataUrl.length > 5_000_000) {
          toast.error("Compressed image still too large. Try a smaller image.");
          setProcessing(false);
          return;
        }
        setForm((f) => ({ ...f, image_url: dataUrl }));
        setProcessing(false);
        toast.success("Image ready.");
      };
      img.onerror = () => {
        setProcessing(false);
        toast.error("Could not load image.");
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setForm((f) => ({ ...f, image_url: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-project-form">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-display text-xl font-bold">
          {isNew ? "New project" : "Edit project"}
        </h3>
        <button
          onClick={onCancel}
          data-testid="admin-project-cancel"
          className="p-2 rounded-full border border-white/[0.1] hover:bg-white/[0.04]"
        >
          <X size={14} />
        </button>
      </div>

      <div className="space-y-5">
        <Input label="Title" value={form.title} onChange={set("title")} testid="admin-form-title" />
        <Input label="Client" value={form.client} onChange={set("client")} testid="admin-form-client" />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Discipline" value={form.discipline} onChange={set("discipline")} placeholder="e.g. AI · Web App" testid="admin-form-discipline" />
          <Input label="Year" value={form.year} onChange={set("year")} testid="admin-form-year" />
        </div>

        {/* Image source selector */}
        <div>
          <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
            Cover image
          </div>
          <div className="flex items-center gap-2 mb-3">
            <ModeTab
              active={imgMode === "url"}
              onClick={() => setImgMode("url")}
              icon={<LinkIcon size={12} />}
              label="Paste URL"
              testid="admin-form-img-mode-url"
            />
            <ModeTab
              active={imgMode === "upload"}
              onClick={() => setImgMode("upload")}
              icon={<Upload size={12} />}
              label="Upload file"
              testid="admin-form-img-mode-upload"
            />
          </div>

          {imgMode === "url" ? (
            <input
              data-testid="admin-form-image"
              type="text"
              value={form.image_url?.startsWith("data:") ? "" : (form.image_url ?? "")}
              onChange={set("image_url")}
              placeholder="https://…"
              className="w-full bg-transparent border-b border-white/[0.12] focus:border-[#29ABE2] py-2 text-white placeholder:text-white/25 outline-none text-sm"
            />
          ) : (
            <div className="border border-dashed border-white/[0.15] rounded-sm p-4 text-center">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleFile}
                data-testid="admin-form-image-upload"
                className="hidden"
                id="admin-image-upload-input"
              />
              <label
                htmlFor="admin-image-upload-input"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-sm cursor-pointer transition-colors"
              >
                <Upload size={12} /> {processing ? "Compressing…" : "Choose image"}
              </label>
              <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mt-3">
                JPG / PNG / WebP · resized to 1600px, max ~4MB
              </div>
            </div>
          )}
        </div>

        {form.image_url && (
          <div className="relative">
            <img
              src={form.image_url}
              alt="preview"
              className="w-full aspect-[16/10] object-cover border border-white/[0.08]"
              onError={(e) => (e.currentTarget.style.opacity = 0.2)}
            />
            <button
              type="button"
              onClick={clearImage}
              className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 backdrop-blur border border-white/[0.1] text-white/80 hover:text-white"
              title="Remove image"
            >
              <X size={12} />
            </button>
          </div>
        )}

        <Input label="Case study URL (optional)" value={form.case_url} onChange={set("case_url")} placeholder="https://…" testid="admin-form-case" />
        <Textarea label="Description" value={form.description} onChange={set("description")} testid="admin-form-description" />

        <div className="grid grid-cols-2 gap-4 items-end">
          <label className="flex items-center gap-3 cursor-pointer pt-2">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={set("featured")}
              data-testid="admin-form-featured"
              className="w-4 h-4 accent-[#29ABE2]"
            />
            <span className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/70">
              Featured on home
            </span>
          </label>
          <Input
            label="Sort order"
            value={form.sort_order}
            onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))}
            type="number"
            testid="admin-form-sort"
          />
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={onCancel}
            data-testid="admin-form-cancel"
            className="px-5 py-2.5 rounded-full border border-white/[0.12] text-sm text-white/80 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            data-testid="admin-form-save"
            disabled={processing}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#29ABE2] text-black text-sm font-medium hover:bg-[#5cc2ec] disabled:opacity-50"
          >
            <Save size={14} /> {isNew ? "Create project" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModeTab({ active, onClick, icon, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-mono-accent text-[10px] uppercase tracking-[0.25em] transition-colors ${
        active
          ? "bg-[#29ABE2] text-black"
          : "border border-white/[0.12] text-white/70 hover:text-white hover:border-white/40"
      }`}
    >
      {icon} {label}
    </button>
  );
}

function Input({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
        {label}
      </div>
      <input
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-white/[0.12] focus:border-[#29ABE2] py-2 text-white placeholder:text-white/25 outline-none text-sm"
      />
    </label>
  );
}

function Textarea({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">
        {label}
      </div>
      <textarea
        data-testid={testid}
        value={value ?? ""}
        onChange={onChange}
        rows={4}
        className="w-full bg-transparent border border-white/[0.12] focus:border-[#29ABE2] p-3 text-white outline-none resize-none text-sm rounded-sm"
      />
    </label>
  );
}
